import CoreBase from '../../../services/engines/CoreBase';
import componentNotifier from '../../../services/wss/ComponentNotifier';
import userStateEngine2 from '../../../controllers/UserState/UserStateEngine2';
import { UserStateKeys } from '../../../controllers/UserState/UserStateKeys';
// ADDED — needed to inject fake doc into cache on simulated upload failure
import attachmentEngine from './AttachmentEngine';

const _name = 'AttachmentStripHandler';

// Event name used for ComponentNotifier — strip state changes
const _event = 'stripStateChanged';

class AttachmentStripHandler extends CoreBase {
    _name = _name;

    // Strip UI state — single source of truth
    collapsed = false;
    showUpload = false;

    // Counter for temporary error RKs — increments per failed upload attempt
    // Resets implicitly on entry change because AttachmentEngine clears its cache
    _errorCount = 0;

    /**
     * Restores collapsed state from UserState on bootup.
     * Call once on app init or on first strip mount.
     */
    init = () => {
        try {
            this.collapsed = userStateEngine2.getValue(UserStateKeys.attachmentStripCollapsed) === true;
        } catch (error) {
            this.logError('init', error.message);
        }
    };

    /**
     * Toggles collapsed state and persists preference to UserState.
     */
    handleToggleCollapse = () => {
        try {
            this.collapsed = !this.collapsed;
            userStateEngine2.set(UserStateKeys.attachmentStripCollapsed, this.collapsed);
            componentNotifier.notify(_event, _name);
        } catch (error) {
            this.logError('handleToggleCollapse', error.message);
        }
    };

    /**
     * Opens the upload dialog.
     */
    handleOpenUpload = () => {
        try {
            this.showUpload = true;
            componentNotifier.notify(_event, _name);
        } catch (error) {
            this.logError('handleOpenUpload', error.message);
        }
    };

    /**
     * Closes the upload dialog.
     */
    handleCloseUpload = () => {
        try {
            this.showUpload = false;
            componentNotifier.notify(_event, _name);
        } catch (error) {
            this.logError('handleCloseUpload', error.message);
        }
    };

    /**
     * Simulates a failed upload.
     * Adds the image to the strip with a temporary error RK and uploadFailed flag.
     * Normal flow: FE posts → BE returns real RK → FE adds to strip.
     * On failure: no RK from BE, so we assign Error_XXXXXXXX as placeholder for retry later.
     * Failed docs are cleared automatically when user switches entry (AttachmentEngine.setEntry).
     * @param {File} file
     */
    handleFakeUpload = (file) => {
        try {
            const fakeDoc = {
                // Temporary RK — startsWith('Error_') can be used to detect failed uploads for retry
                rk: this._nextErrorRk(),
                jid: attachmentEngine.getCurrentJid(),
                // Object URL serves as local preview — not a real storage URL
                url: URL.createObjectURL(file),
                fileName: file.name,
                mimeType: file.type,
                description: '',
                rotation: 0,
                // Flag for visual failed state in AttachmentThumbnail
                uploadFailed: true,
            };

            attachmentEngine.onDocumentsReceived([fakeDoc]);

            // Publish error via CoreBase.onError — picked up by ErrorEngine → ErrorSnack
            this.onError('Could not store — Error pushing to Cloudflare 🔥');

            this.handleCloseUpload();
        } catch (error) {
            this.logError('handleFakeUpload', error.message);
        }
    };

    /**
     * Generates a zero-padded error RK for failed upload placeholders.
     * @returns {string} e.g. 'Error_00000001'
     */
    _nextErrorRk = () => {
        this._errorCount += 1;
        return `Error_${String(this._errorCount).padStart(8, '0')}`;
    };
}

const attachmentStripHandler = new AttachmentStripHandler();
export default attachmentStripHandler;
